/**
 * Logo — lockup compuesto en código: ícono SVG + wordmark "cumpleapp" en texto HTML.
 *
 * El enfoque "lockup en código" reemplaza los SVG horizontales/stacked que
 * contenían taglines de 2 líneas — ilegibles a 28-56px de alto. El texto
 * vivo Inter es nítido a cualquier escala y legible por screen readers.
 *
 * Tono default (fondos claros): "cumple" en cumple-primary-900, "app" en
 * cumple-primary-600 (#5B3FF2). Tono inverse (fondos oscuros, ej AppShell):
 * todo en blanco; el ícono va en chip blanco redondeado para garantizar
 * contraste AA sobre bg-cumple-dark (#0F172A→#1E1B4B).
 *
 * Los SVG horizontales/stacked del kit ya no se usan aquí — se conservan
 * en /assets/logos/ por si el diseñador los necesita como referencia, pero
 * no se importan para evitar warnings de unused import en lint.
 */
import symbolAuditMagnifier from '../assets/logos/symbol-audit-magnifier.svg?url';

export interface LogoProps {
  /** Alto del ícono en píxeles. El wordmark escala proporcional (~0.7×). Default 28. */
  iconHeight?: number;
  /** Tono del lockup según el fondo donde se renderiza. Default 'default'. */
  tone?: 'default' | 'inverse';
  /** Oculta el wordmark — útil para espacios muy pequeños. Default true. */
  showWordmark?: boolean;
  className?: string;
  /** Etiqueta ARIA del logo. Cuando el Logo va dentro de un enlace con su
   *  propio aria-label, pasa ariaLabel="" para evitar doble anuncio. */
  ariaLabel?: string;
}

export function Logo({
  iconHeight = 28,
  tone = 'default',
  showWordmark = true,
  className,
  ariaLabel = 'cumpleapp',
}: LogoProps): JSX.Element {
  // El wordmark escala al 70% del ícono para mantener proporción visual.
  const wordmarkSize = Math.round(iconHeight * 0.7);

  const isInverse = tone === 'inverse';

  // En fondo oscuro (bg-cumple-dark = gradiente #0F172A→#1E1B4B) el ícono
  // coloreado pierde contraste. Lo envolvemos en un chip bg-white con
  // padding proporcional para garantizar contraste AA con el fondo.
  // El chip tiene border-radius 6px — proporcional al tamaño.
  const chipPadding = Math.round(iconHeight * 0.14);
  const chipRadius = Math.round(iconHeight * 0.21);

  return (
    <span
      className={`inline-flex items-center gap-[0.35em] ${className ?? ''}`}
      role={ariaLabel ? 'img' : undefined}
      aria-label={ariaLabel || undefined}
    >
      {isInverse ? (
        // Chip blanco redondeado para que el ícono contraste sobre fondo oscuro
        <span
          className="inline-flex items-center justify-center bg-white flex-shrink-0"
          style={{
            padding: `${chipPadding}px`,
            borderRadius: `${chipRadius}px`,
          }}
          aria-hidden="true"
        >
          <img
            src={symbolAuditMagnifier}
            alt=""
            height={iconHeight}
            width={iconHeight}
            style={{ height: `${iconHeight}px`, width: `${iconHeight}px` }}
            draggable={false}
            aria-hidden="true"
          />
        </span>
      ) : (
        <img
          src={symbolAuditMagnifier}
          alt=""
          height={iconHeight}
          width={iconHeight}
          style={{ height: `${iconHeight}px`, width: `${iconHeight}px` }}
          draggable={false}
          aria-hidden="true"
        />
      )}

      {showWordmark && (
        <span
          className="leading-none select-none"
          style={{
            fontSize: `${wordmarkSize}px`,
            fontWeight: 800,
            letterSpacing: '-0.03em',
            lineHeight: 1,
          }}
          aria-hidden={ariaLabel ? 'true' : undefined}
        >
          <span className={isInverse ? 'text-white' : 'text-cumple-primary-900'}>cumple</span>
          <span className={isInverse ? 'text-white/80' : 'text-cumple-primary-600'}>app</span>
        </span>
      )}
    </span>
  );
}
